import { useEffect, useMemo, useState } from "react";
import { api, API, fmtBRL, fmtDate, STATUS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calendar, FileSpreadsheet, ChevronRight, Search } from "lucide-react";

const brDate = (iso) => iso ? fmtDate(iso + (iso.length === 10 ? "T00:00:00" : "")) : "";
const brMonth = (iso) => { if (!iso) return ""; const [y, m] = iso.split("-"); return `${["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][+m-1]}/${y}`; };

export default function Historico() {
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState(null);
  const [entregas, setEntregas] = useState([]);
  const [lookup, setLookup] = useState({ ent: {}, bai: {}, reg: {}, tip: {}, fpg: {} });
  const [filters, setFilters] = useState({ start: "", end: "", numero_pedido: "" });

  useEffect(() => {
    api.get("/entregas/dates").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      setDays(r.data.filter((d) => d.date < today)); // apenas dias anteriores
    });
    Promise.all([api.get("/entregadores"), api.get("/bairros"), api.get("/regioes"),
                 api.get("/tipos-entrega"), api.get("/formas-pgto")]).then(([e, b, r, t, f]) => {
      const m = (arr) => Object.fromEntries(arr.data.map((x) => [x.id, x.nome]));
      setLookup({ ent: m(e), bai: m(b), reg: m(r), tip: m(t), fpg: m(f) });
    });
  }, []);

  const loadDay = async (date) => {
    setSelected(date);
    const r = await api.get(`/entregas?start=${date}&end=${date}`);
    setEntregas(r.data);
  };

  const filteredDays = useMemo(() => {
    return days.filter((d) => {
      if (filters.start && d.date < filters.start) return false;
      if (filters.end && d.date > filters.end) return false;
      return true;
    });
  }, [days, filters.start, filters.end]);

  const filteredEntregas = useMemo(() => {
    if (!filters.numero_pedido) return entregas;
    const q = filters.numero_pedido.toLowerCase();
    return entregas.filter((it) => (it.numero_pedido || "").toLowerCase().includes(q));
  }, [entregas, filters.numero_pedido]);

  const groupedByYearMonth = useMemo(() => {
    const g = {};
    for (const d of filteredDays) {
      const [y, m] = d.date.split("-");
      g[y] = g[y] || {};
      const key = `${y}-${m}`;
      (g[y][key] = g[y][key] || []).push(d);
    }
    return g;
  }, [filteredDays]);

  // Pré-ordena anos e meses (descrescente) para evitar sort() e Object.entries() em cada render.
  const sortedYears = useMemo(() => {
    return Object.entries(groupedByYearMonth)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([year, months]) => ({
        year,
        months: Object.entries(months).sort(([a], [b]) => b.localeCompare(a)),
        totalDays: Object.values(months).flat().length,
      }));
  }, [groupedByYearMonth]);

  const exportMonth = async (yearMonth) => {
    const [y, m] = yearMonth.split("-");
    const start = `${y}-${m}-01`;
    const lastDay = new Date(+y, +m, 0).getDate();
    const end = `${y}-${m}-${String(lastDay).padStart(2, "0")}`;
    try {
      const r = await fetch(`${API}/reports/xlsx?start=${start}&end=${end}`, { credentials: "include" });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `entregas_${yearMonth}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Exportado ${brMonth(yearMonth + "-01")}`);
    } catch (e) {
      console.error("Falha ao exportar mês", yearMonth, e);
      toast.error(e?.message ? `Erro ao exportar: ${e.message}` : "Erro ao exportar");
    }
  };

  return (
    <div className="space-y-6" data-testid="page-historico">
      <div><h1 className="text-4xl font-extrabold">Histórico</h1>
        <p className="text-muted-foreground">Entregas dos dias anteriores agrupadas por dia</p></div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Filtros</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">De</Label><Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch { /* showPicker não suportado em alguns browsers */ } }} data-testid="hist-filter-start" /></div>
          <div><Label className="text-xs">Até</Label><Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch { /* showPicker não suportado em alguns browsers */ } }} data-testid="hist-filter-end" /></div>
          <div><Label className="text-xs">Nº Pedido</Label>
            <div className="relative"><Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input value={filters.numero_pedido} onChange={(e) => setFilters({ ...filters, numero_pedido: e.target.value })} placeholder="Buscar pedido" className="pl-8" data-testid="hist-filter-pedido" />
            </div></div>
          <div className="flex items-end"><Button variant="ghost" size="sm" onClick={() => setFilters({ start: "", end: "", numero_pedido: "" })} data-testid="hist-clear-filters">Limpar</Button></div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="space-y-4">
          {sortedYears.map(({ year, months, totalDays }) => (
            <div key={year} data-testid={`year-${year}`}>
              <div className="flex items-center gap-2 mb-2 px-1">
                <span className="text-2xl font-extrabold text-orange-700">{year}</span>
                <div className="flex-1 h-px bg-orange-200"></div>
                <span className="text-xs text-muted-foreground">{totalDays} dias</span>
              </div>
              {months.map(([ym, list]) => (
                <Card key={ym} data-testid={`month-${ym}`} className="mb-3">
                  <CardHeader className="pb-2 flex flex-row justify-between items-center">
                    <CardTitle className="text-base capitalize">{brMonth(ym + "-01")}</CardTitle>
                    <Button size="sm" variant="outline" onClick={() => exportMonth(ym)} data-testid={`btn-xlsx-${ym}`}>
                      <FileSpreadsheet className="w-4 h-4 mr-1 text-green-700" />XLSX
                    </Button>
                  </CardHeader>
                  <CardContent className="p-2 space-y-1">
                    {list.map((d) => (
                      <button key={d.date} onClick={() => loadDay(d.date)} data-testid={`day-${d.date}`}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${selected === d.date ? "bg-orange-600 text-white" : "hover:bg-orange-50"}`}>
                        <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{brDate(d.date)}</span>
                        <span className="flex items-center gap-2 text-xs">
                          <Badge variant="secondary" className={selected === d.date ? "bg-white text-orange-700" : ""}>{d.count}</Badge>
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      </button>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          ))}
          {filteredDays.length === 0 && <div className="text-sm text-muted-foreground p-4">{days.length === 0 ? "Sem histórico ainda" : "Nenhum dia com os filtros aplicados"}</div>}
        </div>

        <div className="md:col-span-2">
          {selected ? (
            <Card>
              <CardHeader><CardTitle>Entregas de {brDate(selected)} ({filteredEntregas.length}{filters.numero_pedido ? ` · pedido "${filters.numero_pedido}"` : ""})</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <Table><TableHeader><TableRow>
                  <TableHead>Entregador</TableHead><TableHead>Bairro</TableHead><TableHead>Tipo</TableHead>
                  <TableHead>Nº Pedido</TableHead><TableHead>Valor</TableHead><TableHead>Troco</TableHead>
                  <TableHead>Status</TableHead><TableHead>Motivo</TableHead>
                </TableRow></TableHeader>
                <TableBody>{filteredEntregas.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="font-medium">{lookup.ent[it.entregador_id] || "—"}</TableCell>
                    <TableCell>{lookup.bai[it.bairro_id] || "—"}</TableCell>
                    <TableCell>{lookup.tip[it.tipo_id] || "—"}</TableCell>
                    <TableCell className="mono text-xs">{it.numero_pedido || "—"}</TableCell>
                    <TableCell className="mono">{fmtBRL(it.valor)}</TableCell>
                    <TableCell className="mono text-orange-600">{fmtBRL(it.troco)}</TableCell>
                    <TableCell><Badge className={`status-${it.status} border`}>{STATUS[it.status]}</Badge></TableCell>
                    <TableCell className="text-xs">{it.motivo || "—"}</TableCell>
                  </TableRow>))}
                  {filteredEntregas.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Nenhuma entrega neste dia{filters.numero_pedido ? " com esse pedido" : ""}</TableCell></TableRow>}
                </TableBody></Table>
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="py-16 text-center text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-orange-300" />
              Selecione um dia à esquerda para ver as entregas
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}
